import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Mail, MapPin, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  name: "",
  email: "",
  phone: "",
  company: "",
  event_type: "",
  event_date: "",
  location: "",
  budget: "",
  message: "",
};

export default function BookingForm() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (
      !form.name.trim() ||
      !form.email.trim() ||
      !form.event_type.trim() ||
      !form.message.trim()
    ) {
      toast.error("Bitte alle Pflichtfelder ausfüllen.");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/booking/inquiry`, form);
      toast.success("Anfrage übermittelt — wir melden uns innerhalb von 24h.");
      setSubmitted(true);
      setForm(INITIAL);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Übermittlung fehlgeschlagen. Bitte erneut versuchen."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="relative bg-[#0F0F0F] py-24 md:py-36 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left: pitch */}
          <div className="lg:col-span-5">
            <p className="overline" data-testid="booking-overline">
              <span className="gold-rule mr-4 align-middle" />
              Booking-Anfrage
            </p>
            <h2
              className="font-display mt-4 text-white leading-[1.05] tracking-tight"
              style={{ fontSize: "clamp(2.4rem, 5vw, 4.2rem)", fontWeight: 600 }}
              data-testid="booking-headline"
            >
              Bringen Sie
              <br />
              <span className="italic text-[#D4AF37]">Daniel Lopes</span>
              <br />
              auf Ihre Bühne.
            </h2>

            <p className="mt-8 text-[#C9C9C9] font-body text-base md:text-lg leading-relaxed max-w-md">
              Für Festivals, Galas, TV-Produktionen und Premium-Events.
              Sprechen Sie direkt mit dem Management — wir melden uns
              innerhalb von 24 Stunden mit einem maßgeschneiderten Angebot.
            </p>

            <div className="mt-12 space-y-6 text-[#C9C9C9]">
              <a
                href="mailto:booking@daniellopes.net"
                data-testid="booking-contact-email"
                className="flex items-center gap-4 group hover:text-[#D4AF37] transition-colors"
              >
                <Mail size={18} strokeWidth={1.5} className="text-[#D4AF37]" />
                <span className="font-body text-base">
                  booking@daniellopes.net
                </span>
              </a>
              <div
                className="flex items-center gap-4"
                data-testid="booking-contact-location"
              >
                <MapPin size={18} strokeWidth={1.5} className="text-[#D4AF37]" />
                <span className="font-body text-base">
                  Daniel Lopes Music UG · Tivolistraße 75, 52349 Düren
                </span>
              </div>
            </div>

            <div className="mt-12 border-t border-white/10 pt-8">
              <p className="overline mb-4">Antwortzeit</p>
              <div className="flex items-center gap-3">
                <CheckCircle2
                  size={18}
                  strokeWidth={1.5}
                  className="text-[#D4AF37]"
                />
                <span className="text-[#F5F5F5] font-body">
                  Erstkontakt innerhalb von 24 Stunden — werktags meist sofort.
                </span>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7" data-testid="booking-form-wrap">
            {submitted ? (
              <div
                className="bg-[#0A0A0A] border border-[#D4AF37]/40 p-10 md:p-14 text-center"
                data-testid="booking-success-message"
              >
                <CheckCircle2
                  size={48}
                  strokeWidth={1}
                  className="text-[#D4AF37] mx-auto mb-6"
                />
                <h3 className="font-display text-3xl md:text-4xl text-white mb-4">
                  Danke!
                </h3>
                <p className="text-[#C9C9C9] font-body text-base md:text-lg max-w-md mx-auto leading-relaxed">
                  Ihre Anfrage ist bei uns. Das Management meldet sich
                  innerhalb von 24 Stunden mit allen Details und einem
                  individuellen Angebot.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="btn-secondary mt-10"
                  data-testid="booking-new-inquiry-button"
                >
                  Neue Anfrage senden
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="bg-[#0A0A0A] border border-white/5 p-8 md:p-12 space-y-10"
                data-testid="booking-form"
                noValidate
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                  <div>
                    <label className="luxe-label">Name *</label>
                    <input
                      className="luxe-input"
                      type="text"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Vor- und Nachname"
                      required
                      data-testid="booking-input-name"
                    />
                  </div>
                  <div>
                    <label className="luxe-label">E-Mail *</label>
                    <input
                      className="luxe-input"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="ihre@agentur.de"
                      required
                      data-testid="booking-input-email"
                    />
                  </div>
                  <div>
                    <label className="luxe-label">Telefon</label>
                    <input
                      className="luxe-input"
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="+49 …"
                      data-testid="booking-input-phone"
                    />
                  </div>
                  <div>
                    <label className="luxe-label">Firma / Agentur</label>
                    <input
                      className="luxe-input"
                      type="text"
                      value={form.company}
                      onChange={update("company")}
                      placeholder="Veranstalter / Sender / Agentur"
                      data-testid="booking-input-company"
                    />
                  </div>
                  <div>
                    <label className="luxe-label">Event-Art *</label>
                    <input
                      className="luxe-input"
                      type="text"
                      value={form.event_type}
                      onChange={update("event_type")}
                      placeholder="Festival, Gala, TV-Show, Hochzeit …"
                      required
                      data-testid="booking-input-event-type"
                    />
                  </div>
                  <div>
                    <label className="luxe-label">Wunschdatum</label>
                    <input
                      className="luxe-input"
                      type="text"
                      value={form.event_date}
                      onChange={update("event_date")}
                      placeholder="z. B. 14.06.2026"
                      data-testid="booking-input-date"
                    />
                  </div>
                  <div>
                    <label className="luxe-label">Ort</label>
                    <input
                      className="luxe-input"
                      type="text"
                      value={form.location}
                      onChange={update("location")}
                      placeholder="Stadt, Venue"
                      data-testid="booking-input-location"
                    />
                  </div>
                  <div>
                    <label className="luxe-label">Budget-Rahmen</label>
                    <input
                      className="luxe-input"
                      type="text"
                      value={form.budget}
                      onChange={update("budget")}
                      placeholder="Optional"
                      data-testid="booking-input-budget"
                    />
                  </div>
                </div>

                <div>
                  <label className="luxe-label">Nachricht *</label>
                  <textarea
                    className="luxe-input resize-none"
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Erzählen Sie uns kurz von Ihrem Event — Zielgruppe, Setting, gewünschte Set-Länge …"
                    required
                    data-testid="booking-input-message"
                  />
                </div>

                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 pt-4 border-t border-white/5">
                  <p className="text-xs text-[#A3A3A3] max-w-md">
                    Ihre Daten werden vertraulich behandelt und ausschließlich
                    zur Beantwortung Ihrer Anfrage verwendet.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="booking-submit-button"
                  >
                    {submitting ? "Wird gesendet…" : "Anfrage senden"}
                    {!submitting && <ArrowRight size={16} strokeWidth={1.5} />}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
